import { z } from "zod";

import type {
  CreatePaymentFeePayload,
  FeeScope,
  PaymentFee,
  PaymentFeeKind,
} from "../../types/paymentFees.types";
import { pricePayload, priceShape, refinePrice, seedPrice, type FeeFormMessages } from "./feeFormSchema";

export function buildCorridorPriceSchema(messages: Omit<FeeFormMessages, "corridorRequired" | "validUntil">) {
  return z
    .object({
      target: z.enum(["BROAD", "CORRIDOR"]),
      ...priceShape(messages),
      effectiveNow: z.boolean(),
      validFromDate: z.string(),
      validFromTime: z.string(),
      reason: z.string().max(500),
      alsoRouteIds: z.array(z.string()),
      requiresAck: z.boolean(),
      acknowledged: z.boolean(),
    })
    .superRefine((values, ctx) => {
      if (!values.effectiveNow && !values.validFromDate) {
        ctx.addIssue({ code: "custom", path: ["validFromDate"], message: messages.validFromRequired });
      }
      if (values.requiresAck) {
        if (!values.reason.trim()) {
          ctx.addIssue({ code: "custom", path: ["reason"], message: messages.reasonRequired });
        }
        if (!values.acknowledged) {
          ctx.addIssue({ code: "custom", path: ["acknowledged"], message: messages.ackRequired });
        }
      }
      refinePrice(values, ctx, messages);
    });
}

export type CorridorPriceFormValues = z.input<ReturnType<typeof buildCorridorPriceSchema>>;

/** The fields the first step owns — checked before the review step opens. */
export const PRICE_STEP_FIELDS = [
  "percentage",
  "fixed",
  "minFee",
  "maxFee",
  "tiers",
  "validFromDate",
] as const satisfies readonly (keyof CorridorPriceFormValues)[];

export function seedCorridorPriceForm(
  effective: PaymentFee | null,
  requiresAck: boolean,
): CorridorPriceFormValues {
  return {
    target: "CORRIDOR",
    ...seedPrice(effective),
    effectiveNow: true,
    validFromDate: "",
    validFromTime: "09:00",
    reason: "",
    alsoRouteIds: [],
    requiresAck,
    acknowledged: false,
  };
}

/** Local wall-clock date/time → ISO instant, the shape `valid_from` expects. */
export function toIsoInstant(date: string, time: string): string | null {
  if (!date) return null;
  const parsed = new Date(`${date}T${time || "00:00"}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

export function scopePayload(scope: FeeScope) {
  return {
    ...(scope.provider_code ? { provider_code: scope.provider_code } : {}),
    ...(scope.organization_id ? { organization_id: scope.organization_id } : {}),
    ...(scope.rail ? { rail: scope.rail } : {}),
    ...(scope.country ? { country: scope.country } : {}),
    ...(scope.currency ? { currency: scope.currency } : {}),
    ...(scope.direction ? { direction: scope.direction } : {}),
  };
}

export function buildPricePayload(
  feeKind: PaymentFeeKind,
  scope: FeeScope,
  values: CorridorPriceFormValues,
): CreatePaymentFeePayload {
  const validFrom = values.effectiveNow ? null : toIsoInstant(values.validFromDate, values.validFromTime);
  const reason = values.reason.trim();
  return {
    fee_kind: feeKind,
    ...scopePayload(scope),
    ...pricePayload(values),
    ...(validFrom ? { valid_from: validFrom } : {}),
    ...(reason ? { change_reason: reason } : {}),
  };
}
